// Cloudinary — photo upload + render-time URL transformer.
//
// Browser-direct uploads using an UNSIGNED upload preset, configured in the
// Cloudinary dashboard (Settings → Upload → Add upload preset → Signing
// Mode: Unsigned). The preset is the security boundary: it caps file size,
// restricts formats, and pins the destination folder. Only the cloud name
// and preset NAME ship to the browser; no API secret.

const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

export const cloudinaryConfigured = !!(cloudName && uploadPreset);

export async function uploadPhoto(blob, opts = {}) {
  if (!cloudinaryConfigured) throw new Error('Cloudinary not configured');
  const form = new FormData();
  form.append('file', blob, opts.filename || 'photo.jpg');
  form.append('upload_preset', uploadPreset);
  if (opts.folder) form.append('folder', opts.folder);
  if (opts.publicId) form.append('public_id', opts.publicId);

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
    { method: 'POST', body: form }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Cloudinary upload failed: ${res.status} ${text}`);
  }
  const data = await res.json();
  return {
    url: data.secure_url,
    publicId: data.public_id,
    width: data.width,
    height: data.height,
    bytes: data.bytes,
  };
}

// Inject Cloudinary's auto-format / auto-quality / DPR-aware transforms into
// an existing delivery URL so the browser receives the right encoding for
// its connection and screen. No-op on non-Cloudinary URLs and on URLs that
// already carry a transform segment.
export function cloudinaryRender(url, { width } = {}) {
  if (!url || typeof url !== 'string') return url;
  const m = url.match(/^(https?:\/\/[^/]+\/[^/]+\/image\/upload\/)(.*)$/);
  if (!m) return url;
  const [, prefix, rest] = m;
  // Already has a transform segment (starts with `<letter>_<value>...,/...`).
  if (/^[a-z]_[A-Za-z0-9_,.\-:]+\//.test(rest)) return url;
  const transforms = ['f_auto', 'q_auto', 'dpr_auto'];
  if (width) transforms.push(`w_${width}`);
  return `${prefix}${transforms.join(',')}/${rest}`;
}
